"use client"
import { Button } from "@/components/atoms"
import { LabeledInput } from "@/components/cells"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import useLocaleRouter from "@/hooks/useLocaleRouter"
import { useAppContext } from "@/lib/context/app-context"
import { checkSocialLogin, login } from "@/lib/data/customer"
import { umamiTrackEvent } from "@/lib/utils/umami"
import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslate } from "@tolgee/react"
import { useParams, useRouter, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"
import {
  FieldError,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form"
import { LoginFormData, loginFormSchema } from "./schema"
import { Provider } from "@supabase/supabase-js"
import ButtonGoogleAuthCode from "./components/ButtonGoogleAuthCode"

export const LoginForm = () => {
  const methods = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  return (
    <FormProvider {...methods}>
      <main className="flex items-start justify-center py-[64px] px-[120px]">
        <div className="w-[384px] max-w-full">
          <Form />
          {/* SocialLoginButtons 已隐藏（保留代码），如需恢复取消注释下一行 */}
          {/* <SocialLoginButtons /> */}
        </div>
      </main>
    </FormProvider>
  )
}

const Form = () => {
  const { t } = useTranslate()
  const [error, setError] = useState("")
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useFormContext()
  const localeRouter = useLocaleRouter(useRouter())
  let appContext = useAppContext()
  let searchParams = useSearchParams()
  let from = searchParams.get("from")

  if (from) {
    localStorage.setItem("login-redirect", from)
  }

  const submit = async (data: FieldValues) => {
    const formData = new FormData()
    formData.append("email", data.email)
    formData.append("password", data.password)

    const res = await login(formData)
    if (res) {
      setError(
        res === "INVALID_CREDENTIALS"
          ? t("login_form.invalid_credentials")
          : res === "NOT_A_CUSTOMER"
            ? t("login_form.not_a_customer")
            : /unauthorized|session has expired/i.test(res)
              ? t("login_form.session_expired")
              : res
      )
      return
    }
    setError("")
    appContext.analytics?.("login", {
      method: "Email"
    })
    umamiTrackEvent("login", appContext, { method: "Email" })
    localeRouter.push("/user")
  }

  return (
    <div>
      <h1 className="text-[24px] font-semibold text-[#101828] text-center leading-[32px] mb-[28px]">
        {t("login_form.heading")}
      </h1>

      <form onSubmit={handleSubmit(submit)} className="space-y-[16px]">
        <div>
          <label className="block text-[14px] font-medium text-[#364153] leading-[20px] mb-[6px]">
            {t("login_form.email_label")}
          </label>
          <input
            type="email"
            placeholder={t("login_form.email_placeholder")}
            className="w-full h-[42px] px-[15px] py-[11px] border border-[#d1d5dc] rounded-[8px] text-[14px] text-[#1e2939] leading-[20px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            {...register("email")}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{(errors.email as FieldError)?.message}</p>
          )}
        </div>

        <div>
          <label className="block text-[14px] font-medium text-[#364153] leading-[20px] mb-[6px]">
            {t("login_form.password_label")}
          </label>
          <input
            type="password"
            placeholder={t("login_form.password_placeholder")}
            className="w-full h-[42px] px-[15px] py-[11px] border border-[#d1d5dc] rounded-[8px] text-[14px] text-[#1e2939] leading-[20px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            {...register("password")}
          />
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">{(errors.password as FieldError)?.message}</p>
          )}
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}

        <div className="flex justify-end pt-[8px] pb-[20px] !mt-0">
          <LocalizedClientLink
            href="/forgot-password"
            className="text-[14px] text-[#2b7fff] leading-[20px] hover:underline"
          >
            {t("login_form.forgot_password")}
          </LocalizedClientLink>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-[40px] bg-[#101828] text-white text-[14px] font-medium leading-[20px] rounded-[8px] hover:bg-[#2a3340] disabled:opacity-50 disabled:cursor-not-allowed !mt-0"
        >
          {t("login_form.login_button")}
        </button>

        <p className="text-center text-[14px] leading-[20px]">
          <span className="text-[#6a7282]">{t("login_form.no_account_yet")} </span>
          <LocalizedClientLink href="/user/register" className="font-medium text-[#101828] hover:underline">
            {t("login_form.sign_up")}
          </LocalizedClientLink>
        </p>
      </form>
    </div>
  )
}

const SocialLoginButtons = () => {
  const {
    formState: { errors, isSubmitting },
  } = useFormContext()
  const { t } = useTranslate()
  let { locale } = useParams()

  const loginWithSupabase = async (provider: Provider) => {
    let { isLoggedIn, toAuthUrl } = await checkSocialLogin("supabase-auth", {
      provider,
      redirectTo: `${process.env.NEXT_PUBLIC_AUTH_ENDPOINT ?? window.location.origin}/${locale}/callback/supabase?from=${window.location.origin}`
    })
    if (isLoggedIn) {
      location.reload()
    } else {
      window.location.href = toAuthUrl;
    }
  }

  return (
    <div className="pt-[4px] space-y-[20px]">
      <p className="text-center text-[14px] text-[#99a1af] leading-[20px]">
        {t("login_form.or_login_with")}
      </p>

      <div className="space-y-[12px]">
        <button
          onClick={() => loginWithSupabase("google")}
          disabled={isSubmitting}
          className="w-full h-[42px] flex items-center justify-center gap-[12px] border border-[#e5e7eb] rounded-[8px] bg-white text-[14px] font-medium text-[#364153] leading-[20px] hover:bg-[#f9fafb] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <img src="/google-social-icon.svg" alt="Google" className="w-[18px] h-[18px]" />
          {t("login_form.login_with_google")}
        </button>

        <button
          onClick={() => loginWithSupabase("facebook")}
          disabled={isSubmitting}
          className="w-full h-[42px] flex items-center justify-center gap-[12px] border border-[#e5e7eb] rounded-[8px] bg-white text-[14px] font-medium text-[#364153] leading-[20px] hover:bg-[#f9fafb] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <img src="/fbe/facebook-icon.svg" alt="Facebook" className="w-[18px] h-[18px]" />
          {t("login_form.login_with_facebook")}
        </button>

        <button
          onClick={() => loginWithSupabase("twitter")}
          disabled={isSubmitting}
          className="w-full h-[42px] flex items-center justify-center gap-[12px] border border-[#e5e7eb] rounded-[8px] bg-white text-[14px] font-medium text-[#364153] leading-[20px] hover:bg-[#f9fafb] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <img src="/fbe/twitter-icon.svg" alt="Twitter" className="w-[18px] h-[18px]" />
          {t("login_form.login_with_twitter")}
        </button>
      </div>
    </div>
  )
}
